import Cookies from 'js-cookie';

import { ATTRIBUTION_COOKIE_DOMAIN } from '@lib/attribution/cookies';
import { UUID_PATTERN } from '@lib/uuid';

/**
 * FFH-2368: keep an anonymous visitor's PostHog `distinct_id` stable across
 * hard page loads.
 *
 * PostHog is initialised with `persistence: 'memory'` so nothing is stored
 * before consent, which means the SDK mints a fresh random `distinct_id` on
 * every load. Consent then calls `set_config({ persistence: 'localStorage' })`,
 * and posthog-js handles a persistence change by carrying the CURRENT in-memory
 * props into the new store and saving them, so the fresh id overwrites the
 * stored one rather than resuming it. Every anonymous load therefore became a
 * new person.
 *
 * The fix is to bootstrap the id from a first-party cookie we own, which is
 * readable synchronously at init while persistence is still in memory.
 *
 * The cookie is written ONLY once analytics consent has been granted. Nothing
 * is stored pre-consent, which is the guarantee `persistence: 'memory'` exists
 * to provide, and no continuity is lost: capture is opted out until consent, so
 * an unconsented visitor emits no events for an identity to be stable across.
 *
 * Known and accepted: because flag evaluation is deliberately consent-exempt,
 * a returning visitor's bootstrapped id rides the init-time flags request
 * during the CookieYes settle window. The cookie's existence encodes a prior
 * grant, so this is consented identity; a visitor who has since enabled DNT
 * sends at most one such request before the post-settle revoke clears it.
 *
 * This is deliberately a SEPARATE cookie from `_aid` (the anonymous
 * attribution id used for Meta CAPI and the Auth0 anon-to-known stitch, see
 * lib/auth0/aid-login.ts): `_aid` is set server-side by ffh-public-api on an
 * API response, so it is not guaranteed to exist at first paint, which is
 * exactly when the bootstrap needs a value. It also has its own consent
 * story, whereas the anonymous id must appear and disappear with analytics
 * consent alone.
 */

/**
 * FFH-3561: renamed from `ph_anon_id`. Until then `identify()` merged the
 * anonymous id into the member's PostHog person at sign-in, and PostHog bills
 * every event on an id that belongs to a person at the identified rate, even
 * one sent personless. The new name gives every browser an id no person has
 * claimed.
 */
export const ANONYMOUS_ID_COOKIE = 'ph_anon_id_v2';

const LEGACY_ANONYMOUS_ID_COOKIE = 'ph_anon_id';

/**
 * FFH-3561: members are identified as `member_` plus their profileId, never
 * the bare profileId. Every bare profileId already belongs to a PostHog
 * person, so its events would bill at the identified rate however they are
 * sent; the prefixed id has no person behind it. The app builds the identical
 * string (FFH-3563), so a member keeps one id across web, app and devices.
 */
const MEMBER_DISTINCT_ID_PREFIX = 'member_';

export const buildMemberDistinctId = (profileId: string): string =>
  `${MEMBER_DISTINCT_ID_PREFIX}${profileId}`;

/**
 * Matches PostHog's own `cookie_expiration` default so a returning visitor is
 * recognised for as long as the SDK would have recognised them via localStorage.
 */
const ANONYMOUS_ID_COOKIE_EXPIRES_DAYS = 365;

/**
 * Hosts under this suffix share the apex-domain cookie so the id survives
 * subdomain hops (www. / app. / staging.).
 */
const PRODUCTION_HOST_SUFFIX = 'fantasyfootballhub.co.uk';

/**
 * Apex domain on production hosts, host-only everywhere else. A browser
 * silently rejects a cookie whose Domain is not a suffix of the current host,
 * so pinning the apex domain unconditionally would leave the whole mechanism
 * inert (and invisible in QA) on localhost and *.vercel.app previews.
 *
 * The match is anchored to a label boundary: a bare endsWith would classify a
 * lookalike host such as evilfantasyfootballhub.co.uk as production (the
 * insecure-suffix anti-pattern), and this helper also drives the logout-time
 * deletion in middleware.ts.
 *
 * Exported for the middleware, which expires this cookie on /auth/logout and
 * must scope the deletion exactly as the write was scoped.
 */
export const resolveAnonymousIdCookieDomain = (
  hostname: string
): string | undefined =>
  hostname === PRODUCTION_HOST_SUFFIX ||
  hostname.endsWith(`.${PRODUCTION_HOST_SUFFIX}`)
    ? ATTRIBUTION_COOKIE_DOMAIN
    : undefined;

/**
 * Single source for the write AND the remove: js-cookie only deletes a cookie
 * when the domain/path match the write byte-for-byte (it overrides `expires`
 * itself), so building the attributes twice is how a consent revoke silently
 * stops forgetting the visitor. Falsy attributes (an undefined domain off
 * production) are skipped by js-cookie rather than serialised.
 */
const buildCookieAttributes = () => ({
  domain: resolveAnonymousIdCookieDomain(window.location.hostname),
  path: '/',
  expires: ANONYMOUS_ID_COOKIE_EXPIRES_DAYS,
  secure: window.location.protocol === 'https:',
  sameSite: 'lax' as const
});

/**
 * The id is fed straight back into `posthog.init({ bootstrap })`, so a junk or
 * tampered cookie value must be rejected rather than trusted. SDK-generated
 * ids are RFC 9562 UUIDs (v7 in posthog-js 1.379.2). A member's distinct id
 * carries the `member_` prefix, so the pattern rejects it too, but the real
 * guarantee comes from lib/posthog capturing the id at init, before any
 * identify() can have run.
 */

/** The stored anonymous id, or undefined when absent or not a plausible id. */
export const readAnonymousDistinctId = (): string | undefined => {
  if (typeof window === 'undefined') return undefined;

  const value = Cookies.get(ANONYMOUS_ID_COOKIE);
  if (!value || !UUID_PATTERN.test(value)) return undefined;

  return value;
};

export const persistAnonymousDistinctId = (distinctId: string): void => {
  if (typeof window === 'undefined') return;
  if (!UUID_PATTERN.test(distinctId)) return;

  Cookies.set(ANONYMOUS_ID_COOKIE, distinctId, buildCookieAttributes());
};

export const clearAnonymousDistinctId = (): void => {
  if (typeof window === 'undefined') return;

  Cookies.remove(ANONYMOUS_ID_COOKIE, buildCookieAttributes());
};

/**
 * FFH-3561: delete the pre-rename cookie. Safe before consent, since removing
 * an identifier needs none, and otherwise an id tied to a member's person
 * would sit on the device for up to a year. It was written with the same
 * attributes, so the same scope deletes it.
 */
export const clearLegacyAnonymousDistinctId = (): void => {
  if (typeof window === 'undefined') return;
  if (Cookies.get(LEGACY_ANONYMOUS_ID_COOKIE) === undefined) return;

  Cookies.remove(LEGACY_ANONYMOUS_ID_COOKIE, buildCookieAttributes());
};
